import axios from 'axios';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

// Define the types for configuration items
export interface ConfigItem {
  id?: string;
  name: string;
  value: string | number;
  value_type?: string;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  timestamp?: string;
}

// Define the response type for fetching configurations
export interface ConfigResponse {
  configurations: ConfigItem[];
  count: number;
  timestamp: string;
}

// Define the state interface
interface ConfigState {
  configurations: ConfigItem[];
  count: number;
  timestamp: string;
  loading: boolean;
  error: string | null;
}

const initialState: ConfigState = {
  configurations: [],
  count: 0,
  timestamp: '',
  loading: false,
  error: null,
};

// Set the API base URL
const CONFIG_API_URL = 'https://api.status.gridbit.live/api/v1/config';

// Create async thunk for fetching all configurations
export const fetchAllConfigs = createAsyncThunk(
  'config/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(CONFIG_API_URL);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching configurations:', error);
      return rejectWithValue(error.response?.data || error.message || 'Failed to fetch configurations');
    }
  }
);

// Create async thunk for fetching a specific configuration
export const fetchConfigByName = createAsyncThunk(
  'config/fetchByName',
  async (name: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${CONFIG_API_URL}/${name}`);
      return response.data;
    } catch (error: any) {
      console.error(`Error fetching configuration '${name}':`, error);
      return rejectWithValue(error.response?.data || error.message || `Failed to fetch configuration '${name}'`);
    }
  }
);

// Create async thunk for updating/creating a configuration
export const updateConfig = createAsyncThunk(
  'config/update',
  async (config: { name: string, value: string | number }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(CONFIG_API_URL, config, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Refresh all configurations after update
      await dispatch(fetchAllConfigs());
      
      return response.data;
    } catch (error: any) {
      console.error('Error updating configuration:', error);
      return rejectWithValue(error.response?.data || error.message || 'Failed to update configuration');
    }
  }
);

// Create the configuration slice
export const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handle fetchAllConfigs
      .addCase(fetchAllConfigs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllConfigs.fulfilled, (state, action: PayloadAction<ConfigResponse>) => {
        state.loading = false;
        state.configurations = action.payload.configurations;
        state.count = action.payload.count;
        state.timestamp = action.payload.timestamp;
      })
      .addCase(fetchAllConfigs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Handle fetchConfigByName
      .addCase(fetchConfigByName.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConfigByName.fulfilled, (state, action: PayloadAction<ConfigItem>) => {
        state.loading = false;
        // Update or add the fetched configuration to the state
        const existingIndex = state.configurations.findIndex(
          config => config.name === action.payload.name
        );
        
        if (existingIndex >= 0) {
          state.configurations[existingIndex] = action.payload;
        } else {
          state.configurations.push(action.payload);
        }
      })
      .addCase(fetchConfigByName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Handle updateConfig
      .addCase(updateConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateConfig.fulfilled, (state) => {
        state.loading = false;
        toast.success('Configuration updated successfully');
      })
      .addCase(updateConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error('Failed to update configuration');
      });
  },
});

export default configSlice.reducer;
